import React, { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { X, Camera, RotateCw, AlertCircle } from "lucide-react";

/**
 * CameraScannerModal
 *
 * Opens a fullscreen camera view and decodes 1D + 2D barcodes (Code-128, EAN,
 * UPC, QR, Datamatrix, Aztec, PDF417, etc.) via html5-qrcode.
 *
 * Works on:
 *  - iOS Safari 14.5+   (back camera, requires HTTPS — preview URL is HTTPS)
 *  - Android Chrome     (back camera by default)
 *  - Desktop webcams    (single available device)
 *
 * Props:
 *  - onDetected(code): fires once with the decoded text; modal auto-closes
 *  - onClose(): close without scanning
 */
export default function CameraScannerModal({ onDetected, onClose }) {
  const containerId = "qr-reader-region";
  const scannerRef = useRef(null);
  const [error, setError] = useState("");
  const [cameras, setCameras] = useState([]);
  const [activeCam, setActiveCam] = useState(null);
  const [running, setRunning] = useState(false);

  // Start / restart scanner whenever activeCam changes
  useEffect(() => {
    let cancelled = false;

    async function setup() {
      try {
        // Discover available cameras (first call also triggers permission prompt)
        if (!cameras.length) {
          const devs = await Html5Qrcode.getCameras();
          if (cancelled) return;
          if (!devs?.length) {
            setError("No camera detected on this device.");
            return;
          }
          setCameras(devs);
          // Prefer back camera on phones
          const back = devs.find((d) => /back|rear|environment/i.test(d.label)) || devs[devs.length - 1];
          setActiveCam(back.id);
          return; // effect re-runs once activeCam is set
        }

        if (!activeCam) return;

        if (!scannerRef.current) {
          scannerRef.current = new Html5Qrcode(containerId, { verbose: false });
        }
        await scannerRef.current.start(
          activeCam,
          { fps: 12, qrbox: { width: 280, height: 160 }, aspectRatio: 1.6 },
          (decoded) => {
            if (decoded) {
              onDetected(decoded);
            }
          },
          () => {} // ignore per-frame decode failures
        );
        setRunning(true);
      } catch (err) {
        if (cancelled) return;
        const msg = (err?.message || String(err)).toLowerCase();
        if (msg.includes("permission") || msg.includes("notallowed"))
          setError("Camera permission denied. Allow access in your browser settings and reload.");
        else if (msg.includes("secure context") || msg.includes("https"))
          setError("Camera requires HTTPS. Use the deployed preview URL or a secure tunnel.");
        else setError(err?.message || "Could not start camera.");
        console.warn("[CameraScanner] setup failed:", err);
      }
    }

    setup();

    return () => {
      cancelled = true;
      if (scannerRef.current && running) {
        scannerRef.current.stop().catch((e) => console.warn("[CameraScanner] stop on unmount:", e)).finally(() => {
          try { scannerRef.current.clear(); } catch (e) { console.warn("[CameraScanner] clear on unmount:", e); }
        });
        setRunning(false);
      }
    };
  }, [activeCam]);

  const close = async () => {
    try {
      if (scannerRef.current && running) {
        await scannerRef.current.stop();
        scannerRef.current.clear();
      }
    } catch (e) {
      console.warn("[CameraScanner] close failed:", e);
    }
    onClose();
  };

  const switchCamera = () => {
    if (cameras.length < 2) return;
    const idx = cameras.findIndex((c) => c.id === activeCam);
    const next = cameras[(idx + 1) % cameras.length];
    if (scannerRef.current && running) {
      scannerRef.current.stop().then(() => { scannerRef.current.clear(); setActiveCam(next.id); }).catch(() => setActiveCam(next.id));
    } else {
      setActiveCam(next.id);
    }
  };

  return (
    <div className="fixed inset-0 bg-zinc-950 z-50 flex flex-col" data-testid="camera-scanner-modal">
      <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800 bg-zinc-950">
        <div className="flex items-center gap-2 text-white">
          <Camera className="w-4 h-4" />
          <span className="label-eyebrow text-zinc-400">Camera scanner</span>
        </div>
        <div className="flex items-center gap-2">
          {cameras.length > 1 && (
            <button data-testid="camera-switch-button" onClick={switchCamera} className="text-white border border-zinc-700 px-3 py-1.5 text-xs uppercase tracking-wider hover:bg-zinc-800 inline-flex items-center gap-1">
              <RotateCw className="w-3 h-3" /> Switch
            </button>
          )}
          <button data-testid="camera-close-button" onClick={close} className="text-white border border-zinc-700 px-3 py-1.5 text-xs uppercase tracking-wider hover:bg-zinc-800 inline-flex items-center gap-1">
            <X className="w-3 h-3" /> Close
          </button>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center bg-black p-4 relative">
        <div id={containerId} className="w-full max-w-md" />
        {/* Frame overlay */}
        {!error && (
          <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
            <div className="border-2 border-[#002FA7] w-72 h-44 max-w-[80vw]">
              <div className="absolute left-1/2 -translate-x-1/2 -translate-y-8 text-white text-xs font-mono uppercase tracking-wider opacity-80">
                Aim at a barcode or QR
              </div>
            </div>
          </div>
        )}
        {error && (
          <div className="text-center text-white max-w-md p-6 bg-zinc-900 border border-rose-700">
            <AlertCircle className="w-6 h-6 mx-auto mb-2 text-rose-400" />
            <p className="text-sm">{error}</p>
            <p className="text-xs text-zinc-400 mt-2">You can still type or paste a barcode in the original input.</p>
          </div>
        )}
      </div>

      <div className="px-4 py-3 border-t border-zinc-800 bg-zinc-950 text-center">
        <p className="text-xs font-mono uppercase tracking-wider text-zinc-500">
          Supports Code-128 · EAN · UPC · QR · Datamatrix · Aztec
        </p>
      </div>
    </div>
  );
}
